import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { createChannelUrl } from "../constants";

const createChannelRequestSchema = z.object({
  facilityId: z.string(),
  agentId: z.string(),
});

const createChannelResponseSchema = z.any(); // API doesn't return specific data

interface EnsureChatChannelParams {
  workplaceId: string;
  workerId: string;
}

/**
 * Mutation hook to ensure a chat channel exists between workplace and worker
 * and that the current workplace user is a member.
 *
 * Tries to fetch the channel from Sendbird first. On any failure — whether the
 * channel doesn't exist (`400_111`) or the current user isn't a member
 * (generic `400`) — POSTs to `/chat/channels`, which is idempotent on the
 * backend and both creates the channel if missing and (re-)adds the workplace
 * user as a member. Then re-fetches from Sendbird.
 *
 * @example
 * ```tsx
 * const { mutate: ensureChannel, isPending } = useEnsureChatChannel();
 *
 * ensureChannel({
 *   workplaceId: "workplace123",
 *   workerId: "worker456",
 * });
 * ```
 */
export function useEnsureChatChannel() {
  const queryClient = useQueryClient();
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  return useMutation({
    mutationFn: async ({ workplaceId, workerId }: EnsureChatChannelParams) => {
      if (!sdk) {
        throw new Error("Sendbird SDK not initialized");
      }

      const channelUrl = createChannelUrl(workplaceId, workerId);

      try {
        return await sdk.groupChannel.getChannel(channelUrl);
      } catch {
        await post({
          url: `${environmentConfig.REACT_APP_BASE_API_URL}/chat/channels`,
          data: {
            facilityId: workplaceId,
            agentId: workerId,
          },
          requestSchema: createChannelRequestSchema,
          responseSchema: createChannelResponseSchema,
        });

        return sdk.groupChannel.getChannel(channelUrl);
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate the channel query to refetch with the new channel
      void queryClient.invalidateQueries({
        queryKey: ["chat", "channel", variables.workplaceId, variables.workerId],
      });
    },
    meta: {
      userErrorMessage: "Failed to create chat channel",
    },
  });
}
